import { Component, Input, OnInit, Optional } from '@angular/core';
import { Router } from '@angular/router';
import { NzModalRef } from 'ng-zorro-antd/modal';
import { TranslocoService } from '@ngneat/transloco';
import { ChargebeeService } from '../../../core/services/chargebee.service';
import { SettingService } from '../../../core/services/setting.service';
import { AuthService } from '../../auth/services/auth.service';
import { NzMessageService } from 'ng-zorro-antd/message';
import { SETTINGS } from '../../../core/config/common.settings';

declare var Chargebee: any;

@Component({
  selector: 'app-subscription',
  templateUrl: './subscription.component.html',
  styleUrls: ['./subscription.component.scss'],
})
export class SubscriptionComponent implements OnInit {
  @Input() isUpgrade = false;
  protected readonly SETTINGS = SETTINGS;
  org: any;
  listOfPlans: any [] = [];
  loading = false;

  selectedCycle = this.SETTINGS.PERIOD_UNIT.MONTH;
  selectedPlan: any = null;
  sliderValue = 1;

  constructor(
    private chargebeeService: ChargebeeService,
    private settingService: SettingService,
    private authService: AuthService,
    private messageService: NzMessageService,
    private translocoService: TranslocoService,
    private router: Router,
    @Optional() private modalRef: NzModalRef,
  ) {}

  ngOnInit(): void {
    this.loading = true;
    this.chargebeeService.getListOfPlansByProductId().then(r => {
      this.loading = false;
      this.listOfPlans = [...r].reverse();
      const getDefaultPlanIndex = this.getPlanByCycle(this.selectedCycle);
      this.onSelectedPlan(this.listOfPlans[getDefaultPlanIndex]);
    } );
  }

  async findBrand(): Promise<any> {
    try {
      const user = await this.authService.getLoggedInUser();
      return await this.settingService.findBrand(user.userId);
    } catch (e) {}
  }

  async createdHostedPage(): Promise<void> {
    try {
      Chargebee.init({
        site: SETTINGS.CHARGEBEE_SITE,
        isItemsModel: true,
      });
      const cbInstance = Chargebee.getInstance();
      const hostedPageData = await this.chargebeeService.createHostedPage(this.selectedPlan.id);
      if (hostedPageData) {
        cbInstance.openCheckout({
          hostedPage: () => {
            return Promise.resolve({ ...hostedPageData.hosted_page });
          },
          loaded: () => {
            console.log('loaded');
          },
          success: (hostedPageId: any): void => {
            console.log('Successful checkout');
            // Redirect to your custom page after success after 2 seconds
            const baseUrl = window.location.origin;
            window.location.href = `${baseUrl}/admin`;
          },
          error: (error: any) => {
            this.messageService.error(
              error?.message ||
                this.translocoService.translate(
                  'chargebee.subscription.messages.checkoutFailed'
                )
            );
            console.error(error);
          },
        });
      }
    } catch (error: any) {
      this.messageService.error(
        error?.message ||
          this.translocoService.translate(
            'chargebee.subscription.messages.checkoutFailed'
          )
      );
    }
  }

  async upgradeToYearly(): Promise<void> {
    if (this.selectedCycle === this.SETTINGS.PERIOD_UNIT.MONTH) {
      this.modalRef.close();
    }else{
      try {
        this.loading = true;
        await this.chargebeeService.upgradeTrialToYearly({ yearlyItemPriceId: this.selectedPlan.id });
        this.messageService.success(
          this.translocoService.translate('chargebee.subscription.messages.upgradeSuccess')
        );
        this.modalRef.close('upgraded');
      } catch (error: any) {
        this.messageService.error(
          error?.message ||
            this.translocoService.translate('chargebee.subscription.messages.checkoutFailed')
        );
      } finally {
        this.loading = false;
      }
    }
  }

  convertNumber(value: number): string {
    return (value / 100).toFixed(2);
  }

  onSelectedPlan(plan: any): void {
    this.selectedCycle = plan.item_price.period_unit;
    this.selectedPlan = plan.item_price;
  }

  calculateTieredPrice(units: number, tiers: any): string {
    let total = 0;
    let remaining = units;

    for (const tier of tiers) {
      if (remaining <= 0) { break; }

      const tierStart = tier.starting_unit;
      const tierEnd = tier.ending_unit ?? units; // if no ending_unit, use max units
      const maxUnitsInTier = tierEnd - tierStart + 1;

      if (units >= tierStart) {
        const unitsInThisTier = Math.min(remaining, maxUnitsInTier);
        total += unitsInThisTier * tier.price;
        remaining -= unitsInThisTier;
      }
    }
    return  this.convertNumber(total); // in cents
  }

  getPlanByCycle(cycle: string): number {
    return this.listOfPlans.findIndex(
      p => p.item_price.period_unit === cycle
    );
  }

  amountSavedInYear(): number {
    const amountPerMonth = +this.calculateTieredPrice(this.sliderValue, this.selectedPlan?.tiers ?? []);
    const getDefaultPlanIndex = this.getPlanByCycle(this.SETTINGS.PERIOD_UNIT.YEAR);
    const yearPlanTiers = this.listOfPlans[getDefaultPlanIndex]?.item_price?.tiers ?? [];
    const amountPerYear = +this.calculateTieredPrice(this.sliderValue, yearPlanTiers);
    return (amountPerMonth * 12) - amountPerYear;
  }

  amountInYear(): number {
    const getDefaultPlanIndex = this.getPlanByCycle(this.SETTINGS.PERIOD_UNIT.YEAR);
    const yearPlanTiers = this.listOfPlans[getDefaultPlanIndex]?.item_price?.tiers ?? [];
    return +this.calculateTieredPrice(this.sliderValue, yearPlanTiers);
  }
}
